import React from 'react';
import { Link } from 'react-router-dom';
import HeaderSection from '../content/HeaderSection';
import FooterSection from '../content/FooterSection';
import useGoogleAnalytics from '../hooks/useGoogleAnalytics';
import { usePageLanguage } from '../context/PageLanguageContext';
import SEOHead from '../components/SEOHead';

// Translation object
const translations = {
  en: {
    pageTitle: 'Changelog - Rink Hockey Scoreboard',
    pageDescription: 'Version history and updates for the Rink Hockey Scoreboard application. See all new features, improvements, and bug fixes.',
    title: 'Changelog',
    subtitle: 'Version history and updates',
    backToHome: 'Back to Home',
    feature: 'Feature',
    improvement: 'Improvement',
    bugfix: 'Bug Fix',
    testing: 'Testing'
  },
  de: {
    pageTitle: 'Changelog - Rink Hockey Scoreboard',
    pageDescription: 'Versionshistorie und Updates für die Rink Hockey Scoreboard Anwendung. Sehen Sie alle neuen Funktionen, Verbesserungen und Fehlerbehebungen.',
    title: 'Changelog',
    subtitle: 'Versionshistorie und Updates',
    backToHome: 'Zurück zur Startseite',
    feature: 'Feature',
    improvement: 'Verbesserung',
    bugfix: 'Fehlerbehebung',
    testing: 'Testing'
  }
};

const Changelog = () => {
  useGoogleAnalytics();
  const { pageLanguage } = usePageLanguage();
  const t = translations[pageLanguage] || translations.en;
  const L = (en, de) => (pageLanguage === 'de' ? de : en);
  const versions = [
    {
      version: 'Desktop 1.2.3',
      date: {
        en: 'October 25, 2026',
        de: '25. Oktober 2026'
      },
      title: {
        en: 'Sponsors that stay, music that waits',
        de: 'Sponsoren, die bleiben, Musik, die wartet'
      },
      changes: [
        {
          type: 'bugfix',
          title: {
            en: 'Sponsor images survive a restart',
            de: 'Sponsorenbilder überstehen einen Neustart'
          },
          description: {
            en: 'With several sponsor images, only the first one was still there after restarting the app — and the break overlay showed none. They are now stored where there is room for them.',
            de: 'Bei mehreren Sponsorenbildern war nach einem Neustart nur noch das erste da — und das Pausen-Overlay zeigte gar keine. Sie werden jetzt dort gespeichert, wo genug Platz für sie ist.'
          }
        },
        {
          type: 'improvement',
          title: {
            en: 'Sponsors live in Media & Teams and in the backup',
            de: 'Sponsoren in Medien & Teams und im Backup'
          },
          description: {
            en: 'The sponsor images for the scoreboard and the break overlay have their own “Sponsors” area in Media & Teams, next to the rest of the club’s media, and travel with the configuration backup.',
            de: 'Die Sponsorenbilder für Scoreboard und Pausen-Overlay haben einen eigenen Bereich „Sponsoren“ in Medien & Teams, neben den übrigen Medien des Vereins, und sind im Konfigurations-Backup enthalten.'
          }
        },
        {
          type: 'improvement',
          title: {
            en: 'The break playlist starts by itself',
            de: 'Die Pausen-Playlist startet von selbst'
          },
          description: {
            en: 'With a YouTube playlist configured, it starts in every period break — and its row shows it playing and stops it, like the other music rows.',
            de: 'Ist eine YouTube-Playlist eingetragen, startet sie in jeder Drittelspause — und ihre Zeile zeigt, dass sie läuft, und stoppt sie, wie die anderen Musik-Zeilen.'
          }
        },
        {
          type: 'bugfix',
          title: {
            en: 'Stoppage music only after a real stoppage',
            de: 'Unterbruchsmusik nur nach einem echten Unterbruch'
          },
          description: {
            en: 'Automatic music now starts only when the game clock was stopped and not restarted within the threshold. Ending a break no longer starts it, and an emptied threshold field no longer means “immediately”.',
            de: 'Automatische Musik startet jetzt nur, wenn die Spieluhr gestoppt und innerhalb des Schwellwerts nicht wieder gestartet wurde. Das Ende einer Pause startet sie nicht mehr, und ein geleertes Schwellwert-Feld heisst nicht mehr „sofort“.'
          }
        },
        {
          type: 'improvement',
          title: {
            en: 'A red music button instead of a popping card',
            de: 'Ein roter Musik-Knopf statt aufspringender Karte'
          },
          description: {
            en: 'Automatic music shows the music card only the first time after the app opened. After that the music button turns red while anything plays. On Windows the button now shows a music note instead of a play symbol.',
            de: 'Automatische Musik zeigt die Musik-Karte nur beim ersten Mal nach dem Öffnen der App. Danach ist der Musik-Knopf rot, solange etwas läuft. Auf Windows zeigt der Knopf jetzt eine Musiknote statt eines Wiedergabe-Symbols.'
          }
        }
      ]
    },
    {
      version: 'Desktop 1.2.2',
      date: {
        en: 'October 16, 2026',
        de: '16. Oktober 2026'
      },
      title: {
        en: 'The music card behaves like a menu',
        de: 'Die Musik-Karte verhält sich wie ein Menü'
      },
      changes: [
        {
          type: 'improvement',
          title: {
            en: 'One button opens and closes the music card',
            de: 'Ein Knopf öffnet und schliesst die Musik-Karte'
          },
          description: {
            en: 'The round music button works like the two menus: press to open, press to close, and the card appears above it. It no longer pops open on its own when something starts, no longer disappears after five seconds, and has no minimize button. While the hall has sound, the button is blue — so you can see it with the card closed.',
            de: 'Der runde Musik-Knopf funktioniert wie die beiden Menüs: drücken öffnet, drücken schliesst, und die Karte erscheint darüber. Sie springt nicht mehr von selbst auf, verschwindet nicht mehr nach fünf Sekunden und hat keinen Minimieren-Knopf mehr. Solange in der Halle etwas läuft, ist der Knopf blau — das siehst du auch bei geschlossener Karte.'
          }
        },
        {
          type: 'improvement',
          title: {
            en: 'The music that plays is its own stop button',
            de: 'Die laufende Musik ist ihr eigener Stopp-Knopf'
          },
          description: {
            en: 'The row you pressed to start turns red, says “stop” and names the track. The separate stop button above the list is gone — one place per source, press to start, press to stop.',
            de: 'Die Zeile, die du zum Starten gedrückt hast, wird rot, sagt „stoppen" und nennt den Titel. Der separate Stopp-Knopf über der Liste ist weg — ein Ort pro Quelle, drücken startet, drücken stoppt.'
          }
        },
        {
          type: 'improvement',
          title: {
            en: 'Music the app starts announces itself',
            de: 'Musik, die die App selbst startet, meldet sich'
          },
          description: {
            en: 'When the automation starts a track at a stoppage, or a period break fills itself with your playlist, the card shows itself for five seconds and then closes again — so you can see what started without hunting for a stop button.',
            de: 'Startet die Automatik bei einem Unterbruch einen Track, oder füllt sich eine Drittelspause mit deiner Playlist, zeigt sich die Karte fünf Sekunden lang und schliesst dann wieder — du siehst, was läuft, ohne den Stopp-Knopf suchen zu müssen.'
          }
        },
        {
          type: 'bugfix',
          title: {
            en: 'A playlist on a looping clip is shuffled',
            de: 'Playlist beim Loop-Video läuft zufällig'
          },
          description: {
            en: 'A YouTube playlist attached to a looping pre-game clip now starts at a random track, like the break music. It used to open every match with the same song.',
            de: 'Eine YouTube-Playlist an einem Loop-Clip vor dem Spiel startet neu bei einem zufälligen Titel, wie die Pausenmusik. Vorher begann jedes Spiel mit demselben Song.'
          }
        }
      ]
    },
    {
      version: 'Desktop 1.2.1',
      date: {
        en: 'October 11, 2026',
        de: '11. Oktober 2026'
      },
      title: {
        en: 'The playlist plays, and the buttons look like buttons',
        de: 'Die Playlist spielt, und die Knöpfe sehen aus wie Knöpfe'
      },
      changes: [
        {
          type: 'bugfix',
          title: {
            en: 'The YouTube playlist plays again',
            de: 'Die YouTube-Playlist spielt wieder'
          },
          description: {
            en: 'The installed app showed the playlist as running while the hall stayed silent. YouTube was refusing the address the app played from — it accepts the name “localhost” but not the bare IP behind it. Nothing to do on your side: enter your playlist as before.',
            de: 'Die installierte App zeigte die Playlist als laufend, in der Halle blieb es still. YouTube lehnte die Adresse ab, von der die App abspielt — es akzeptiert den Namen „localhost", nicht aber die nackte IP dahinter. Für dich ändert sich nichts: Playlist eintragen wie bisher.'
          }
        },
        {
          type: 'improvement',
          title: {
            en: 'A blocked song is skipped, not endured',
            de: 'Ein gesperrter Song wird übersprungen'
          },
          description: {
            en: 'Some labels forbid playing their videos outside YouTube. In a shuffled playlist that used to mean silence at a random moment; now the player simply moves on to the next song. If a whole playlist is blocked, the card says so — and also tells apart a wrong link, a deleted playlist and a missing internet connection.',
            de: 'Manche Labels verbieten das Abspielen ihrer Videos ausserhalb von YouTube. In einer gemischten Playlist hiess das bisher: Stille zu einem beliebigen Zeitpunkt. Jetzt springt der Player einfach zum nächsten Song. Ist eine ganze Playlist gesperrt, sagt es die Karte — und unterscheidet neu auch falschen Link, gelöschte Playlist und fehlendes Internet.'
          }
        },
        {
          type: 'improvement',
          title: {
            en: 'Buttons you can see',
            de: 'Knöpfe, die man sieht'
          },
          description: {
            en: 'On the music card and in the right menu the actions were pale text on white and easy to miss. They are tiles now: grey when ready, blue while that source is playing, dashed when there is nothing to play yet.',
            de: 'Auf der Musik-Karte und im rechten Menü waren die Aktionen blasser Text auf Weiss und leicht zu übersehen. Jetzt sind es Kacheln: grau wenn bereit, blau während diese Quelle läuft, gestrichelt wenn noch nichts hinterlegt ist.'
          }
        }
      ]
    },
    {
      version: 'Desktop 1.2.0',
      date: {
        en: 'October 9, 2026',
        de: '9. Oktober 2026'
      },
      title: {
        en: 'The board waits for you',
        de: 'Die Anzeigetafel wartet auf dich'
      },
      changes: [
        {
          type: 'improvement',
          title: {
            en: 'A goal counts the point — and nothing else',
            de: 'Ein Tor zählt den Punkt — sonst nichts'
          },
          description: {
            en: 'Recording a goal no longer stops the clocks and no longer starts the music by itself. The celebration is offered when play stops: straight away with the clock stopped, and at the next stoppage for a goal scored during play.',
            de: 'Ein Tor stoppt die Uhren nicht mehr und startet auch keine Musik mehr von selbst. Gefeiert wird beim Unterbruch: sofort bei gestoppter Uhr, sonst beim nächsten Unterbruch für ein Tor, das während des Spiels fiel.'
          }
        },
        {
          type: 'feature',
          title: {
            en: 'A general celebration',
            de: 'Allgemeiner Torjubel'
          },
          description: {
            en: 'Above Skip there is now one button for the celebration and the music without naming a player — for a goal whose scorer you did not see, or who is not in the roster.',
            de: 'Über „Überspringen“ gibt es neu einen Knopf für Jubel und Musik ohne Namen — für ein Tor, dessen Schützin du nicht gesehen hast oder die nicht im Kader steht.'
          }
        },
        {
          type: 'improvement',
          title: {
            en: 'Everything about music in one place',
            de: 'Alles zur Musik an einem Ort'
          },
          description: {
            en: 'The play buttons no longer sit in two places. Settings live in Media & Teams next to the tracks; starting, stopping and the volume live on the music button at the desk. Both are split into “around the match” (the YouTube playlist for the break and before/after) and “during the match” (your own MP3s), so it is clear which music plays when.',
            de: 'Die Knöpfe gibt es nicht mehr doppelt. Die Einstellungen liegen in „Medien & Teams" bei der Musik, das Starten, Stoppen und die Lautstärke beim Musik-Knopf am Pult. Beide sind in „Rund ums Spiel" (YouTube-Playlist für Pause und vor/nach dem Spiel) und „Während des Spiels" (deine MP3s) gegliedert — so ist klar, welche Musik wann läuft.'
          }
        },
        {
          type: 'feature',
          title: {
            en: 'Music on one button',
            de: 'Musik auf einen Knopf'
          },
          description: {
            en: 'The automatic music is off on a new installation. The playback button above the menu is then always there: one press opens the card with the stoppage music, the goal music and the break playlist, one more starts a randomly chosen track.',
            de: 'Die automatische Musik ist bei einer neuen Installation aus. Dafür ist der Musik-Knopf über dem Menü immer da: Ein Druck öffnet die Karte mit Unterbruch-Musik, Tor-Musik und Pausen-Playlist, ein weiterer startet einen zufällig gewählten Track.'
          }
        }
      ]
    },
    {
      version: 'Desktop 1.1.0',
      date: {
        en: 'September 17, 2026',
        de: '17. September 2026'
      },
      title: {
        en: 'Shutout celebration, and who scored after a player clip',
        de: 'Shutout-Jubel, und wer getroffen hat nach dem Spieler-Clip'
      },
      changes: [
        {
          type: 'feature',
          title: {
            en: 'Shutout celebration for the goalkeeper',
            de: 'Shutout-Jubel für die Torhüterin'
          },
          description: {
            en: 'Mark goalkeepers in the roster. When the home team wins without conceding, the app asks at the final whistle which goalkeeper played and celebrates them — with their video from goal media, or the drawn animation with a word of your choice, and the goal music. Goalkeepers no longer appear in the goal picker.',
            de: 'Markiere im Kader die Torhüterinnen. Gewinnt das Heimteam ohne Gegentor, fragt die App beim Schlusspfiff, wer im Tor stand, und feiert sie — mit ihrem Video aus den Tor-Medien oder der gezeichneten Animation mit einem Wort deiner Wahl, und mit der Tor-Musik. In der Auswahl beim Heimtor erscheinen Torhüterinnen nicht mehr.'
          }
        },
        {
          type: 'feature',
          title: {
            en: 'Animation after a player clip',
            de: 'Animation nach dem Spieler-Clip'
          },
          description: {
            en: 'A player’s own celebration video rarely shows their number. With one switch, the drawn animation with number and name follows straight after it, so the hall knows who scored.',
            de: 'Ein eigenes Jubelvideo zeigt selten die Nummer. Mit einem Schalter folgt direkt danach die gezeichnete Animation mit Nummer und Name — so weiss die Halle, wer getroffen hat.'
          }
        },
        {
          type: 'feature',
          title: {
            en: 'Music the operator starts',
            de: 'Musik auf Knopfdruck'
          },
          description: {
            en: 'The club’s YouTube playlist and the stoppage tracks can be started from the right menu at any time — before the first whistle or after the last — with the automation still on.',
            de: 'Die YouTube-Playlist des Vereins und die Unterbruch-Musik lassen sich jederzeit im rechten Menü starten — vor dem Anpfiff oder nach dem Schlusspfiff — und die Automatik bleibt eingeschaltet.'
          }
        },
        {
          type: 'improvement',
          title: {
            en: 'A tidier Media & Teams screen',
            de: 'Übersichtlichere Seite «Medien & Teams»'
          },
          description: {
            en: 'Every area opens and closes with a click on its heading; only the first one is open to begin with. Teams show as a compact list with crest and roster size and open when you need them.',
            de: 'Jeder Bereich klappt mit einem Klick auf die Überschrift auf und zu; zu Beginn ist nur der erste offen. Teams erscheinen als kompakte Liste mit Wappen und Kadergrösse und öffnen sich, wenn du sie brauchst.'
          }
        },
        {
          type: 'bugfix',
          title: {
            en: 'Horn key, scrollbars and a translated picker',
            de: 'Horn-Taste, Scrollbalken und übersetzte Auswahl'
          },
          description: {
            en: 'The "b" key sounds the horn again in the installed app, the control window on Windows no longer shows scrollbars, and the "Who scored?" picker follows the app language.',
            de: 'Die Taste „b" löst das Horn in der installierten App wieder aus, das Steuerfenster zeigt unter Windows keine Scrollbalken mehr, und die Auswahl „Wer hat getroffen?" folgt der App-Sprache.'
          }
        }
      ]
    },
    {
      version: '1.1.4',
      date: {
        en: 'October 14, 2025',
        de: '14. Oktober 2025'
      },
      title: {
        en: 'Enhanced Auto-Break Overlay Transition',
        de: 'Erweiterte Auto-Break Overlay Transition'
      },
      changes: [
        {
          type: 'improvement',
          title: {
            en: 'Two-stage auto-break display',
            de: 'Zweistufige Auto-Break Anzeige'
          },
          description: {
            en: 'First 30 seconds: smaller overlay without sponsors (w-1/3, smaller font) for a less intrusive display. After 30 seconds: automatic transition to the full overlay with sponsor carousel.',
            de: 'Erste 30 Sekunden: Kleineres Overlay ohne Sponsoren (w-1/3, kleinere Schrift) für weniger aufdringliche Anzeige. Nach 30 Sekunden: Automatischer Übergang zum vollständigen Overlay mit Sponsor-Karussell.'
          }
        },
        {
          type: 'improvement',
          title: {
            en: 'Intelligent overlay size adjustment',
            de: 'Intelligente Overlay-Größenanpassung'
          },
          description: {
            en: 'Reduced text sizes (4vh title, 12vh timer) in the first phase for a focused display. Seamless transition to standard sizes (8vh/20vh) after 30 seconds.',
            de: 'Reduzierte Textgrößen (4vh Titel, 12vh Timer) in der ersten Phase für fokussierte Darstellung. Nahtloser Übergang zu Standard-Größen (8vh/20vh) nach 30 Sekunden.'
          }
        },
        {
          type: 'improvement',
          title: {
            en: 'Selective application',
            de: 'Selektive Anwendung'
          },
          description: {
            en: 'Enhancement applies only to automatic breaks (Auto-Break enabled). Manual breaks still show the full overlay immediately. Full backwards compatibility.',
            de: 'Enhancement gilt nur für automatische Breaks (Auto-Break aktiviert). Manuelle Breaks zeigen weiterhin sofort das vollständige Overlay. Vollständige Rückwärtskompatibilität.'
          }
        }
      ]
    },
    {
      version: '1.1.3',
      date: {
        en: 'October 1, 2025',
        de: '1. Oktober 2025'
      },
      title: {
        en: 'Tab navigation in Settings & Beta Features',
        de: 'Tab-Navigation in Settings & Beta Features'
      },
      changes: [
        {
          type: 'feature',
          title: {
            en: 'Tab navigation in Settings',
            de: 'Tab-Navigation in Settings'
          },
          description: {
            en: 'New clear tab structure with three areas: General Settings (timer, colors, sound, sponsors), Media License (entertainment license), Beta Features (experimental functions)',
            de: 'Neue übersichtliche Tab-Struktur mit drei Bereichen: General Settings (Timer, Farben, Sound, Sponsoren), Media License (Entertainment Lizenz), Beta Features (experimentelle Funktionen)'
          }
        },
        {
          type: 'feature',
          title: {
            en: 'Beta Features section',
            de: 'Beta Features Sektion'
          },
          description: {
            en: 'New dedicated area for experimental features. All beta features are disabled by default and must be enabled manually. Clear experimental labeling with descriptions.',
            de: 'Neuer dedizierter Bereich für experimentelle Features. Alle Beta-Features sind standardmäßig deaktiviert und müssen manuell aktiviert werden. Klare Kennzeichnung als experimentell mit Beschreibungen.'
          }
        },
        {
          type: 'feature',
          title: {
            en: 'Sixth sponsor image',
            de: 'Sechstes Sponsorenbild'
          },
          description: {
            en: 'Increase sponsor slots from 5 to 6 images. All images rotate automatically in the carousel on the scoreboard.',
            de: 'Erhöhung der Sponsor-Slots von 5 auf 6 Bilder. Alle Bilder rotieren automatisch im Carousel auf dem Scoreboard.'
          }
        },
        {
          type: 'improvement',
          title: {
            en: 'Reorganized Settings',
            de: 'Reorganisierte Settings'
          },
          description: {
            en: 'Auto Break and Team Inferiority Display moved to Beta Features. Entertainment License in a separate Media License tab. Logical grouping of all settings.',
            de: 'Auto Break und Team Inferiority Display in Beta Features verschoben. Entertainment License in separaten Media License Tab. Logische Gruppierung aller Einstellungen.'
          }
        },
        {
          type: 'improvement',
          title: {
            en: 'Changed defaults',
            de: 'Geänderte Standardwerte'
          },
          description: {
            en: 'Auto Break and Team Inferiority Display are now disabled by default (previously enabled). Users must explicitly enable beta features in Settings → Beta Features.',
            de: 'Auto Break und Team Inferiority Display sind jetzt standardmäßig deaktiviert (vorher aktiviert). Benutzer müssen Beta-Features explizit in Settings → Beta Features aktivieren.'
          }
        },
        {
          type: 'testing',
          title: {
            en: 'Adjusted E2E tests',
            de: 'Angepasste E2E-Tests'
          },
          description: {
            en: 'Tests now navigate to the corresponding Settings tabs and enable beta features in beforeEach. Updated selectors for the tab-based structure.',
            de: 'Tests navigieren jetzt zu den entsprechenden Settings-Tabs und aktivieren Beta-Features im beforeEach. Aktualisierte Selektoren für Tab-basierte Struktur.'
          }
        }
      ]
    },
    {
      version: '1.1.2',
      date: {
        en: 'September 30, 2025',
        de: '30. September 2025'
      },
      title: {
        en: 'Auto-Break toggle & Break Selection modal',
        de: 'Auto-Break Toggle & Break Selection Modal'
      },
      changes: [
        {
          type: 'feature',
          title: {
            en: 'Auto-Break toggle',
            de: 'Auto-Break Toggle'
          },
          description: {
            en: 'New toggle in Settings to enable/disable automatic breaks on period changes. Default: enabled for backwards compatibility.',
            de: 'Neuer Toggle in den Settings um automatische Breaks bei Periodenwechseln ein-/auszuschalten. Standard: aktiviert für Rückwärtskompatibilität.'
          }
        },
        {
          type: 'feature',
          title: {
            en: 'Break Selection modal',
            de: 'Break Selection Modal'
          },
          description: {
            en: 'New modal for manual break selection with three buttons (P1→P2, P2→P3, P3→P4). Shows configured durations and enables targeted break activation.',
            de: 'Neues Modal zur manuellen Break-Auswahl mit drei Buttons (P1→P2, P2→P3, P3→P4). Zeigt konfigurierte Dauern an und ermöglicht gezielte Break-Aktivierung.'
          }
        },
        {
          type: 'improvement',
          title: {
            en: 'Flexible break system',
            de: 'Flexibles Break-System'
          },
          description: {
            en: 'All period transitions (including P1→P2) now respect the Auto-Break toggle. Manual breaks can be started independently of the toggle.',
            de: 'Alle Periodenwechsel (inkl. P1→P2) respektieren jetzt den Auto-Break Toggle. Manuelle Breaks können unabhängig vom Toggle gestartet werden.'
          }
        },
        {
          type: 'improvement',
          title: {
            en: 'Optimized Settings UI',
            de: 'Optimierte Settings UI'
          },
          description: {
            en: 'Auto-Break toggle positioned in the two-column layout next to Break P3→P4. Consistent design with the buzzer toggle.',
            de: 'Auto-Break Toggle im zweispaltigen Layout neben Break P3→P4 positioniert. Konsistentes Design mit Buzzer Toggle.'
          }
        },
        {
          type: 'testing',
          title: {
            en: 'Extended E2E tests',
            de: 'Erweiterte E2E-Tests'
          },
          description: {
            en: 'New break-selection-modal.spec.js with 8 tests. Updated tests for Auto-Break toggle and improved checkbox selection.',
            de: 'Neue break-selection-modal.spec.js mit 8 Tests. Angepasste Tests für Auto-Break Toggle und verbesserte Checkbox-Selektion.'
          }
        }
      ]
    },
    {
      version: '1.1.1',
      date: {
        en: 'September 29, 2025',
        de: '29. September 2025'
      },
      title: {
        en: 'Configurable break durations & team inferiority calculation',
        de: 'Konfigurierbare Break-Dauern & Unterzahlberechnung'
      },
      changes: [
        {
          type: 'feature',
          title: {
            en: 'Separate break durations for each period transition',
            de: 'Separate Break-Dauern für jeden Periodenwechsel'
          },
          description: {
            en: 'Three individually configurable break durations: P1→P2 (default: 10 min), P2→P3 (default: 3 min), P3→P4 (default: 2 min)',
            de: 'Drei individuell konfigurierbare Break-Dauern: P1→P2 (Standard: 10 Min), P2→P3 (Standard: 3 Min), P3→P4 (Standard: 2 Min)'
          }
        },
        {
          type: 'feature',
          title: {
            en: 'Automatic break duration selection',
            de: 'Automatische Break-Dauer-Auswahl'
          },
          description: {
            en: 'The system automatically selects the correct break duration based on the current period transition',
            de: 'Das System wählt automatisch die richtige Break-Dauer basierend auf dem aktuellen Periodenwechsel'
          }
        },
        {
          type: 'feature',
          title: {
            en: 'Team inferiority calculation for timekeepers',
            de: 'Unterzahlzeit-Berechnung (Team Inferiority) für Zeitnehmende'
          },
          description: {
            en: 'Automatic cumulation of penalty times: 1 penalty = penalty time, 2+ penalties = sequential penalties for the team. Real-time countdown synced with game timer.',
            de: 'Automatische Kumulierung der Strafzeiten: 1 Strafe = Strafzeit, ab 2 Strafen = Sequentielle Strafzeiten für das Team. Echtzeit-Countdown synchronisiert mit Spieltimer.'
          }
        },
        {
          type: 'improvement',
          title: {
            en: 'Improved Settings UI',
            de: 'Verbesserte Settings UI'
          },
          description: {
            en: 'Two-column layout for break settings and clearer labels (Default Period, Overtime Period, Break P1→P2/P2→P3/P3→P4)',
            de: 'Zweispaltiges Layout für Break-Einstellungen und klarere Beschriftungen (Default Period, Overtime Period, Break P1→P2/P2→P3/P3→P4)'
          }
        },
        {
          type: 'improvement',
          title: {
            en: 'Enhanced break system',
            de: 'Enhanced Break System'
          },
          description: {
            en: 'Breaks now start automatically for all period transitions (P1→P2, P2→P3, P3→P4) when overtime logic allows a transition',
            de: 'Breaks werden jetzt für alle Periodenwechsel (P1→P2, P2→P3, P3→P4) automatisch gestartet wenn die Overtime-Logik einen Wechsel erlaubt'
          }
        },
        {
          type: 'testing',
          title: {
            en: 'Comprehensive E2E tests',
            de: 'Umfassende E2E-Tests'
          },
          description: {
            en: 'New tests for all break transitions with automatic timer adjustment and board reset for better test isolation',
            de: 'Neue Tests für alle Break-Übergänge mit automatischer Timer-Anpassung und Board-Reset für bessere Test-Isolation'
          }
        }
      ]
    },
    {
      version: '1.1.0',
      date: {
        en: 'September 29, 2025',
        de: '29. September 2025'
      },
      title: {
        en: 'Smart team foul indicators & improvements',
        de: 'Intelligente Teamfoul-Markierungen & Verbesserungen'
      },
      changes: [
        {
          type: 'feature',
          title: {
            en: 'Smart team foul indicators',
            de: 'Intelligente Teamfoul-Markierungen'
          },
          description: {
            en: 'Automatic detection of the brand color with alternative yellow/orange markers for reddish colors for better visibility',
            de: 'Automatische Erkennung der Brand Color mit alternativer gelb/orange Markierung bei rötlichen Farben für bessere Sichtbarkeit'
          }
        },
        {
          type: 'feature',
          title: {
            en: 'Buzzer volume control',
            de: 'Buzzer-Lautstärke Kontrolle'
          },
          description: {
            en: 'Adjustable buzzer volume from 0-200% in Settings with localStorage persistence',
            de: 'Einstellbare Buzzer-Lautstärke von 0-200% in den Settings mit localStorage-Persistierung'
          }
        },
        {
          type: 'improvement',
          title: {
            en: 'Consistent LiveView',
            de: 'Konsistente LiveView'
          },
          description: {
            en: 'Team foul indicators now also use the smart color logic in LiveView',
            de: 'Teamfoul-Markierungen verwenden jetzt auch in der LiveView die intelligente Farblogik'
          }
        },
        {
          type: 'testing',
          title: {
            en: 'Extended E2E tests',
            de: 'E2E-Tests erweitert'
          },
          description: {
            en: 'Comprehensive tests for team foul functionality including warning colors and buzzer sounds',
            de: 'Umfassende Tests für Teamfoul-Funktionalität inklusive Warnung-Farben und Buzzer-Sounds'
          }
        }
      ]
    },
    {
      version: '1.0.0',
      date: {
        en: 'September 22, 2025',
        de: '22. September 2025'
      },
      title: {
        en: 'First stable release',
        de: 'Erste stabile Version'
      },
      changes: [
        {
          type: 'feature',
          title: {
            en: 'Complete scoreboard system',
            de: 'Vollständiges Scoreboard System'
          },
          description: {
            en: 'Complete rink hockey scoreboard functionality with timer, scores, team fouls and penalties',
            de: 'Komplette Rink Hockey Scoreboard Funktionalität mit Timer, Scores, Teamfouls und Penalties'
          }
        },
        {
          type: 'feature',
          title: {
            en: 'LiveView mode',
            de: 'LiveView Modus'
          },
          description: {
            en: 'Separate LiveView for spectators with optimized display',
            de: 'Separate LiveView für Zuschauer mit optimierter Darstellung'
          }
        },
        {
          type: 'feature',
          title: {
            en: 'Entertainment system',
            de: 'Entertainment System'
          },
          description: {
            en: 'Pre-game videos, in-game music and sponsor carousel',
            de: 'PreGame Videos, In-Game Musik und Sponsor-Karussell'
          }
        },
        {
          type: 'feature',
          title: {
            en: 'Timeout management',
            de: 'Timeout Management'
          },
          description: {
            en: 'Full timeout management with visual overlays',
            de: 'Vollständige Timeout-Verwaltung mit visuellen Overlays'
          }
        },
        {
          type: 'feature',
          title: {
            en: 'Personalization',
            de: 'Personalisierung'
          },
          description: {
            en: 'Customizable brand colors and sponsor images',
            de: 'Anpassbare Brand Colors und Sponsor-Bilder'
          }
        },
        {
          type: 'feature',
          title: {
            en: 'Buzzer system',
            de: 'Buzzer System'
          },
          description: {
            en: 'Automatic buzzer sounds at end of game and for team fouls',
            de: 'Automatische Buzzer-Sounds bei Spielende und Teamfouls'
          }
        }
      ]
    }
  ];

  const getTypeIcon = (type) => {
    switch (type) {
      case 'feature':
        return '🆕';
      case 'improvement':
        return '⚡';
      case 'bugfix':
        return '🐛';
      case 'testing':
        return '🧪';
      default:
        return '📝';
    }
  };

  const getTypeColor = (type) => {
    switch (type) {
      case 'feature':
        return 'bg-green-100 text-green-800';
      case 'improvement':
        return 'bg-blue-100 text-blue-800';
      case 'bugfix':
        return 'bg-red-100 text-red-800';
      case 'testing':
        return 'bg-purple-100 text-purple-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getTypeLabel = (type) => {
    switch (type) {
      case 'feature':
        return t.feature;
      case 'improvement':
        return t.improvement;
      case 'bugfix':
        return t.bugfix;
      case 'testing':
        return t.testing;
      default:
        return type;
    }
  };

  return (
  <div className="min-h-screen flex flex-col">
    <SEOHead 
      title={t.pageTitle}
      description={t.pageDescription}
      lang={pageLanguage}
      canonical="https://scoreboard.tomroethlisberger.ch/changelog"
    />
    <HeaderSection />
    <main className="flex-grow bg-gray-50">
      <div className="max-w-4xl mx-auto px-4 py-8">
        <div className="mb-8">
          <Link to="/" className="text-blue-600 hover:text-blue-800 mb-4 inline-block">
            ← {t.backToHome}
          </Link>
          <h1 className="text-4xl font-bold text-gray-900 mb-2">{t.title}</h1>
          <p className="text-gray-600">{t.subtitle}</p>
        </div>
        <div className="space-y-8">
          {versions.map((version, index) => (
            <div key={version.version} className="bg-white rounded-lg shadow-md overflow-hidden">
              {/* Version Header */}
              <div className="bg-gradient-to-r from-blue-600 to-blue-700 text-white p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <h2 className="text-2xl font-bold">Version {version.version}</h2>
                    <p className="text-blue-100 mt-1">{L(version.title.en, version.title.de)}</p>
                  </div>
                  <div className="text-right">
                    <div className="text-blue-100 text-sm">{L('Published on', 'Veröffentlicht am')}</div>
                    <div className="text-white font-semibold">{L(version.date.en, version.date.de)}</div>
                  </div>
                </div>
              </div>

              {/* Changes List */}
              <div className="p-6">
                <div className="space-y-4">
                  {version.changes.map((change, changeIndex) => (
                    <div key={changeIndex} className="flex items-start space-x-4 p-4 bg-gray-50 rounded-lg">
                      <div className="flex-shrink-0">
                        <span className="text-2xl">{getTypeIcon(change.type)}</span>
                      </div>
                      <div className="flex-grow">
                        <div className="flex items-center space-x-3 mb-2">
                          <h3 className="font-semibold text-gray-900">{L(change.title.en, change.title.de)}</h3>
                          <span className={`inline-block px-2 py-1 text-xs font-semibold rounded ${getTypeColor(change.type)}`}>
                            {getTypeLabel(change.type)}
                          </span>
                        </div>
                        <p className="text-gray-600">{L(change.description.en, change.description.de)}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Footer */}
        <div className="mt-12 text-center">
          <div className="bg-white rounded-lg shadow-md p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-2">{L('Feedback & Suggestions', 'Feedback & Vorschläge')}</h3>
            <p className="text-gray-600 mb-4">
              {L('Do you have ideas for new features or found a bug?', 'Haben Sie Ideen für neue Features oder haben einen Bug gefunden?')}
            </p>
            <div className="flex justify-center space-x-4">
              <Link 
                to="/manual" 
                className="bg-gray-100 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-200 transition-colors"
              >
                📖 {L('Manual', 'Handbuch')}
              </Link>
              <Link 
                to="/videos" 
                className="bg-gray-100 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-200 transition-colors"
              >
                🎥 {L('Video Guides', 'Video-Anleitungen')}
              </Link>
            </div>
          </div>
        </div>
      </div>
      </main>
      <FooterSection />
    </div>
  );
};

export default Changelog;
